import { Link } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { Bug01Icon, SourceCodeIcon, Alert01Icon, Tag01Icon, PaintBoardIcon, ArrowUp01Icon, Comment01Icon } from '@hugeicons/core-free-icons';
import type { Ticket } from '@overlayqa/shared';
import { SeverityBadge, StatusBadge, TypeBadge } from './TicketBadges';
import { FormattedDescription } from '../../components/shared/FormattedDescription';
import { IssueIdWithCopy } from '../../components/shared/IssueIdWithCopy';
import { log } from '@/lib/logger';
import { OptionalDescriptionEditor } from '@overlayqa/shared/ui';

interface IssueBreadcrumbProps {
  ticket: Ticket;
  ticketId: string;
}

/**
 * Breadcrumb navigation above the issue detail grid.
 */
export function IssueBreadcrumb({ ticket, ticketId }: IssueBreadcrumbProps) {
  log.info('[IssueBreadcrumb] Rendering for issue:', { ticketId });
  return (
    <nav id="issue-breadcrumb" className="flex items-center gap-2 text-sm text-gray-500" aria-label="Breadcrumb">
      <Link to="/projects" className="hover:text-primary-600">
        <span className="text-cap-trim block">Projects</span>
      </Link>
      <span aria-hidden="true">/</span>
      <Link
        to={`/projects/${ticket.projectId}/issues`}
        className="hover:text-primary-600"
      >
        <span className="text-cap-trim block">Issues</span>
      </Link>
      <span aria-hidden="true">/</span>
      <span aria-current="page" className="font-semibold text-gray-900 inline-flex items-center gap-1">
        Issue
        <IssueIdWithCopy
          displayId={ticket.displayId}
          fallback={`#${ticketId?.slice(0, 8) ?? ''}`}
        />
      </span>
    </nav>
  );
}

const TYPE_ICONS: Record<string, typeof Bug01Icon> = {
  'design-bug': Bug01Icon,
  'design-gap': SourceCodeIcon,
  'design-debt': Alert01Icon,
  'design-token': PaintBoardIcon,
  improvement: ArrowUp01Icon,
  general: Comment01Icon,
};

function issueTypeIcon(type?: string | null) {
  return (type && TYPE_ICONS[type]) || Tag01Icon;
}

interface IssueTitleCardProps {
  ticket: Ticket;
  members?: import('@overlayqa/shared').MentionableUser[];
  onSaveDescription?: (description: string) => Promise<unknown>;
}

/**
 * Issue explanation with inline badges; the detail page places it before evidence.
 */
export function IssueTitleCard({ ticket, members, onSaveDescription }: IssueTitleCardProps) {
  return (
    <section id="issue-title" className="card-sm">
      <h2 className="section-label mb-3 flex items-center gap-2"><HugeiconsIcon icon={issueTypeIcon(ticket.type)} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4 text-gray-400" aria-hidden="true" />Issue</h2>
      <h1 className="text-xl font-bold text-gray-900 mb-2 mt-4 break-words">{ticket.title}</h1>
      <div className="flex items-center gap-2 mb-3">
        <SeverityBadge severity={ticket.severity} />
        <StatusBadge status={ticket.status} />
        {ticket.type && (
          <TypeBadge type={ticket.type} />
        )}
      </div>
      <OptionalDescriptionEditor key={ticket.id} value={ticket.description || ''} onSave={onSaveDescription}
        renderValue={description => <FormattedDescription text={description} members={members} />} />
    </section>
  );
}
